import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { OrderProcessingLine, TreeNode, TreeNodeDeclaration } from '@equipmyschool/emsadminweb-models';

@Component({
  selector: 'app-orderline-tree',
  templateUrl: './orderline-tree.component.html',
  styleUrls: ['./orderline-tree.component.scss']
})
export class OrderlineTreeComponent implements OnInit, OnChanges {
  @Input() datasource: OrderProcessingLine[] | null = null;
  @Input() nodes: TreeNodeDeclaration[] = [];
  @Input() selectionChanged: Date | null = null;
  @Output() selectedNodeChange = new EventEmitter<TreeNode>();

  tree: TreeNode[] = [];

  expanded = new Set<string>();

  // map id -> state: 0 unchecked, 1 checked, 2 partial
  states = new Map<TreeNode, number>();

  ngOnInit() {
    // Build tree from flat datasource using `nodes` level definitions.
    // Each entry in `nodes` should have a `name` (property key for label)
    // and optionally an `id` (property key for id). If `id` isn't set,
    // the label string is used as the node id (literal) so selecting it
    // indicates the literal value and should show all matching items.
    this.tree = this.buildTreeFromDatasource(this.datasource || [], this.nodes || []);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['datasource'] || changes['nodes']) {
      this.states.clear();
      this.tree = this.buildTreeFromDatasource(this.datasource || [], this.nodes || []);
      this.updateParents();
    }
    else if (changes['selectionChanged'] && this.selectionChanged) {
      this.updateParents();
    }
  }

  nodeSelected(node: TreeNode) {
    this.selectedNodeChange.emit(node);
  }

  toggleExpand(node: TreeNode) {
    if (!node || !node.id) return;
    if (this.expanded.has(node.id)) this.expanded.delete(node.id);
    else this.expanded.add(node.id);
  }

  isExpanded(node: TreeNode) {
    return node && node.id && this.expanded.has(node.id);
  }

  buildTreeFromDatasource(items: OrderProcessingLine[], levels: TreeNodeDeclaration[]): TreeNode[] {
    if (!levels.length) return [];

    const rootMap: TreeNode[] = this.buildNodes(0, null, items, levels);

    // Convert rootMap to array and clean helper maps
    const result: TreeNode[] = [];
    rootMap.forEach(n => {
      this.cleanupNode(n);
      result.push(n);
    });
    return result;
  }

  buildNodes(levelIndex: number, parentNode: TreeNode | null, items: OrderProcessingLine[], levels: TreeNodeDeclaration[]): TreeNode[] {
    const level = levels[levelIndex];
    const nameKey = level.name;
    const idKey = level.id;
    const aggregates = level.aggregates;
    const display = level.display;
    const sort = level.sort;
    const selectionColumn = level.selectionColumn;
    const nodes: TreeNode[] = [];
    // filter items according to parent criteria
    let filtered = items;
    if (parentNode && parentNode.criteria && parentNode.criteria.length) {
      filtered = items.filter(it => {
        return parentNode!.criteria!.every(c => {
          const val = it[c.name as keyof OrderProcessingLine];
          return String(val) === String(c.id);
        })
      });
    }

    // If idKey is an empty string, create a single node using the property
    // name as the label and do NOT include any criterion for this level.
    if (idKey === '') {
      const node: TreeNode = {
        id: nameKey,
        label: nameKey,
        selected: false,
        aggregates: aggregates,
        display: display,
        sort: sort,
        selectionColumn: selectionColumn,
        criteria: (parentNode && parentNode.criteria) ? [...parentNode.criteria] : []
      } as TreeNode;
      if (levelIndex + 1 < levels.length) {
        node.children = this.buildNodes(levelIndex + 1, node, items, levels);
      }
      nodes.push(node);
      // ensure children are sorted by label
      nodes.sort((a,b) => (a.label || '').localeCompare(b.label || ''));
      return nodes;
    }

    // collect distinct entries for this level by (label,id)
    const map = new Map<string, { label: string, idVal: string }>();
    filtered.forEach(it => {
      const label = nameKey && it[nameKey as keyof OrderProcessingLine] !== undefined ? String(it[nameKey as keyof OrderProcessingLine]) : '';
      const idVal = idKey && it[idKey as keyof OrderProcessingLine] !== undefined ? String(it[idKey as keyof OrderProcessingLine]) : label;
      const key = `${idKey}:${idVal}`;
      if (!map.has(key)) map.set(key, { label, idVal });
    });

    // build nodes from distinct entries
    map.forEach((v) => {
      const node: TreeNode = {
        id: `${idKey}:${v.idVal}`,
        label: v.label,
        selected: false,
        aggregates: aggregates,
        display: display,
        sort: sort,
        selectionColumn: selectionColumn,
        criteria: (parentNode && parentNode.criteria) ? [...parentNode.criteria] : []
      } as TreeNode;
      // append this level's criterion
      node.criteria.push({ name: idKey, id: v.idVal });
      // build children
      if (levelIndex + 1 < levels.length) {
        node.children = this.buildNodes(levelIndex + 1, node, items, levels);
      }
      nodes.push(node);
    });

    // sort nodes by label
    nodes.sort((a,b) => (a.label || '').localeCompare(b.label || ''));

    return nodes;
  }

  cleanupNode(node: TreeNode) {
    const childMap = (node as any).__childMap as Map<string, TreeNode> | undefined;
    if (childMap) {
      node.children = [];
      childMap.forEach(c => {
        this.cleanupNode(c);
        node.children!.push(c);
      });
      delete (node as any).__childMap;
    }
  }

  toggleNodeCheck(node: TreeNode) {
    const filtered = this.datasource?.filter(r => {
      return node.criteria?.every(c => {
        const val = r[c.name as keyof OrderProcessingLine];
        return String(val) === String(c.id);
      });
    });

    const checked = filtered?.every(r => r.Selected) ?? false;
    
    this.datasource?.forEach(r => {
      if (filtered?.includes(r)) {
        r.Selected = !checked;
      }
    });

    this.states.set(node, checked ? 0 : 1);
    this.updateParents();
  }

  updateParents() {
    const recurse = (nodes?: TreeNode[]) => {
      if (!nodes) return;
      nodes.forEach(n => {
        if (n.children && n.children.length) {
          recurse(n.children);
        }
        const filter = this.datasource?.filter(r => {
          return n.criteria.every(c => {
            const val = r[c.name as keyof OrderProcessingLine];
            return String(val) === String(c.id);
          });
        }) ?? [];
        const allChecked = filter.every(s=>s.Selected);
        const noneChecked = filter.every(s=>s.Selected === undefined || s.Selected === false);
        this.states.set(n, allChecked?1: noneChecked?0:2);
      })
    }
    recurse(this.tree);
  }
}
